import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isVoicesRoute } from './voicesRoutes';

// Readiness milestones, not an invented byte-download percentage. Off-screen lazy
// media and third-party services must never hold up the first usable screen.
export function prepareWebsite(root, { onProgress, onReady, timeout = 10000 }) {
  let disposed = false;
  let finishing = false;
  let frame = 0;
  let secondFrame = 0;
  let completed = 0;
  let total = 0;
  const cleanups = [];
  const pendingScenes = new Set();
  const deadline = setTimeout(() => {
    pendingScenes.forEach(scene => scene.querySelector('canvas')?.dispatchEvent(new Event('voicesstartuptimeout')));
    finish(false);
  }, timeout);

  function positionFragment() {
    if (!window.location.hash) return;
    try { document.getElementById(decodeURIComponent(window.location.hash.slice(1)))?.scrollIntoView({ block: 'start', behavior: 'instant' }); }
    catch { /* A malformed fragment must not block entry. */ }
  }

  function finish(complete) {
    if (disposed || finishing) return;
    finishing = true;
    clearTimeout(deadline);
    cancelAnimationFrame(frame);
    if (complete) onProgress(100);
    // Let decoded images, font metrics and the first canvas frame reach paint.
    frame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (!disposed) {
          onReady(complete);
          // Unlocking the page and decoding media can change the fragment offset.
          positionFragment();
          cleanup();
        }
      });
    });
  }
  function cleanup() {
    disposed = true;
    clearTimeout(deadline);
    cancelAnimationFrame(frame);
    cancelAnimationFrame(secondFrame);
    cleanups.forEach(disconnect => disconnect());
  }
  function settled() {
    if (disposed || finishing) return;
    completed++;
    onProgress(10 + Math.floor(completed / total * 85));
    if (completed === total) finish(true);
  }
  function imageReady(image) {
    return new Promise(resolve => {
      let started = false;
      const loaded = () => {
        if (started) return;
        started = true;
        Promise.resolve().then(() => image.decode?.()).catch(() => {}).then(resolve);
      };
      image.addEventListener('load', loaded);
      image.addEventListener('error', loaded);
      cleanups.push(() => { image.removeEventListener('load', loaded); image.removeEventListener('error', loaded); });
      if (image.complete) loaded();
    });
  }
  function sceneReady(scene) {
    return new Promise(resolve => {
      const check = () => {
        const state = scene.dataset.renderer;
        const calm = scene.closest('[data-motion="calm"]');
        const canvas = scene.querySelector('canvas');
        if (calm || state === 'reduced' || state === 'unavailable' ||
            (state === 'ready' && canvas?.hasAttribute('data-progress'))) {
          observer.disconnect();
          pendingScenes.delete(scene);
          resolve();
        }
      };
      const observer = new MutationObserver(check);
      observer.observe(scene.closest('.vj-journey') || scene, { attributes: true, subtree: true, attributeFilter: ['data-renderer', 'data-progress', 'data-motion'] });
      pendingScenes.add(scene);
      cleanups.push(() => observer.disconnect());
      check();
    });
  }
  onProgress(10);
  frame = requestAnimationFrame(() => {
    if (disposed || finishing) return;
    // Resolve entry links behind the cover, before choosing the visible assets.
    positionFragment();
    const inView = node => {
      const box = node.getBoundingClientRect();
      return box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < window.innerHeight;
    };
    const images = Array.from(root.querySelectorAll('img')).filter(image => image.loading !== 'lazy' || inView(image));
    const scenes = Array.from(root.querySelectorAll('.vj-scene, .v-interior-hero')).filter(inView);
    const tasks = [Promise.resolve(document.fonts?.ready), ...images.map(imageReady), ...scenes.map(sceneReady)];
    total = tasks.length;
    tasks.forEach(task => task.then(settled, settled));
  });
  return cleanup;
}

export default function VoicesStartup() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    const startup = window.__antrazalStartup;
    if (!startup || startup.isDone()) return undefined;
    if (!isVoicesRoute(pathname)) { startup.finish(false); return undefined; }
    return prepareWebsite(document.getElementById('root'), { onProgress: startup.update, onReady: startup.finish });
  }, [pathname]);
  return null;
}
